import { useMemo } from "react";
import { getPublicOrigin } from "@/lib/publicOrigin";
import { useSiteContent } from "@/hooks/useSiteContent";

interface Props {
  type: "organization" | "product" | "breadcrumb";
  data?: Record<string, any>;
}

const JsonLdSchema = ({ type, data }: Props) => {
  const { content: settings } = useSiteContent();
  const brandName = settings?.brand_name || "Store";
  const origin = getPublicOrigin();

  const schema = useMemo(() => {
    if (type === "organization") {
      return {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: brandName,
        url: origin,
        logo: `${origin}/logo.png`,
        description: settings?.brand_description || "Premium ecommerce store.",
        sameAs: [],
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "customer service",
          availableLanguage: ["Bengali", "English"],
        },
      };
    }
    if (type === "product" && data) {
      return {
        "@context": "https://schema.org",
        "@type": "Product",
        name: data.name,
        image: data.image,
        description: data.description || data.name,
        sku: data.sku || data.id,
        brand: { "@type": "Brand", name: brandName },
        offers: {
          "@type": "Offer",
          url: data.url,
          priceCurrency: "BDT",
          price: data.offer_price || data.price,
          availability: (data.stock ?? 1) > 0
            ? "https://schema.org/InStock"
            : "https://schema.org/OutOfStock",
          seller: { "@type": "Organization", name: brandName },
        },
        ...(data.rating && {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: data.rating,
            bestRating: 5,
            ratingCount: data.ratingCount || 1,
          },
        }),
      };
    }
    if (type === "breadcrumb" && data?.items) {
      return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: data.items.map((item: any, i: number) => ({
          "@type": "ListItem",
          position: i + 1,
          name: item.name,
          item: item.url,
        })),
      };
    }
    return null;
  }, [type, data]);

  if (!schema) return null;

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

export default JsonLdSchema;
